import { Link, useParams } from "react-router-dom";
import { Reveal } from "@/components/Reveal";
import { useSEO } from "@/components/SEO";

const DOCS = {
  "privacy-policy": {
    title: "Privacy Policy",
    updated: "July 2026",
    body: [
      ["What we collect", "When you submit an enquiry on Loan Salaah, we collect your name, phone number, email address, city, employment type, the loan type you are interested in, and the amount you wish to borrow. If you use the EMI calculator, those inputs stay on your device unless you choose to attach them to an enquiry."],
      ["How we use it", "We use your details to contact you about your enquiry, to shortlist suitable lending partners, and to prepare your loan application if you choose to proceed. We share your information only with banks and NBFCs relevant to your enquiry, and only to the extent needed to process your application."],
      ["What we never do", "We do not sell your personal data to marketers. We do not ask for passwords, OTPs, card numbers or internet-banking credentials over email, SMS or phone — anyone doing so in our name is an imposter; please report it."],
      ["Storage & retention", "Enquiry data is stored on secured systems. We retain it only as long as needed to service your enquiry and to meet legal or partner-compliance requirements."],
      ["Your rights", "You may ask us at any time to correct or delete the personal information we hold about you, or to stop contacting you, by writing to us through the contact form on this website."],
      ["Cookies & analytics", "This site uses privacy-respecting analytics to understand which pages are useful. No advertising trackers are used."],
    ],
  },
  "terms-of-use": {
    title: "Terms of Use",
    updated: "July 2026",
    body: [
      ["Who we are", "Loan Salaah is a Direct Selling Agent (DSA) and loan facilitator/marketplace. We are not a bank, NBFC or lender. We help you compare loan offers from our lending partners and assist with preparing and submitting your application."],
      ["No lending decisions", "All lending decisions — approval, rejection, interest rate, amount, tenure and disbursal — are made solely by the respective banks and NBFCs. Loan Salaah does not influence, guarantee or underwrite any credit decision."],
      ["Indicative information", "Interest rates, EMIs, eligibility criteria and product features shown on this website are indicative and for illustration. They are not offers. Actual terms are confirmed by the lender at the time of sanction."],
      ["Enquiries", "Submitting an enquiry is free and non-binding. It is not a loan application and does not trigger a credit check. A formal application to a lender happens only with your explicit consent."],
      ["Acceptable use", "You agree to provide accurate information in your enquiries and not to misuse this website. Content on this site, including blog articles, is for general information and is not financial advice."],
      ["Liability", "Loan Salaah is not liable for decisions made by lenders, delays in processing, or any loss arising from reliance on indicative figures shown on this website."],
    ],
  },
  disclaimer: {
    title: "Disclaimer",
    updated: "July 2026",
    body: [
      ["DSA disclosure", "Loan Salaah operates as a Direct Selling Agent (DSA) and loan facilitator for its partner banks and Non-Banking Financial Companies (NBFCs). We are compensated by these partners for successful sourcing and facilitation. This compensation is never charged to you as a borrower."],
      ["Rates are indicative", "All interest rates, processing fees, EMIs and savings illustrations on this website — including in the EMI calculator and blog articles — are indicative examples for planning purposes only. Your actual rate is determined by the lender based on your credit profile, income, collateral and prevailing policies."],
      ["No guarantee of approval", "Submission of an enquiry or application does not guarantee loan approval. Sanction and disbursal are entirely at the lender's discretion."],
      ["Not financial advice", "Blog articles and guides on this website are educational content, not investment, tax or legal advice. Please consult a qualified professional for decisions specific to your situation."],
      ["Third-party content", "This website is managed partly through a content management system. While we take care to keep information accurate and current, Loan Salaah does not warrant the completeness of third-party data such as lender rate cards."],
    ],
  },
};

export default function Legal() {
  const { doc } = useParams();
  const entry = DOCS[doc];

  useSEO({
    title: entry ? `${entry.title} | Loan Salaah` : "Legal | Loan Salaah",
    description: entry ? `${entry.title} for the Loan Salaah website.` : "",
  });

  if (!entry) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-40 text-center" data-testid="legal-not-found">
        <h1 className="font-serif text-4xl font-semibold text-concordia">Document not found.</h1>
        <Link to="/" className="mt-6 inline-block rounded-full bg-concordia px-7 py-3 text-sm font-semibold text-ivory">Back to home</Link>
      </div>
    );
  }

  return (
    <div data-testid={`legal-${doc}`} className="pb-24 pt-36">
      <div className="mx-auto max-w-3xl px-4 sm:px-6">
        <Reveal>
          <div className="flex items-center gap-4">
            <span className="brass-rule" aria-hidden="true" />
            <span className="font-mono text-[0.65rem] uppercase tracking-[0.3em] text-brass">Legal · Updated {entry.updated}</span>
          </div>
          <h1 className="mt-5 font-serif text-4xl font-semibold tracking-tight text-concordia sm:text-5xl">{entry.title}</h1>
          <div className="mt-10 space-y-8">
            {entry.body.map(([heading, text]) => (
              <section key={heading}>
                <h2 className="font-serif text-2xl font-semibold text-concordia">{heading}</h2>
                <p className="mt-3 text-base leading-relaxed text-charcoal/80">{text}</p>
              </section>
            ))}
          </div>
        </Reveal>
      </div>
    </div>
  );
}
